import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthError } from 'aws-amplify/auth';
import { useRouter, Href } from 'expo-router';
import { useAtom } from 'jotai';
import { AlertCircleIcon, EyeIcon, EyeOffIcon, X } from 'lucide-react-native';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { z } from 'zod';

import { Heading } from './heading';
import { Button, ButtonText } from './ui/button';
import { Icon } from './ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from './ui/modal';

import { ButtonSpinner } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Input, InputField, InputIcon, InputSlot } from '@/components/ui/input';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { SUPPORT_EMAIL_ADDRESS, SUPPORT_EMAIL_LINK } from '@/constants/links';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { useColors } from '@/hooks/useColors';
import { useCreateAccount } from '@/hooks/useCreateAccount';
import { useEmailLink } from '@/hooks/useEmailLink';
import {
  buildAuthPathWithRedirect,
  buildSignupPathWithRedirect,
  useCurrentPagePath,
} from '@/hooks/useRedirectPath';
import { useToaster } from '@/hooks/useToaster';
import { communityShowAuthModalAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { trackLoginFailed, trackLoginSuccess } from '@/util/analyticsEvents';
import { getLoginFailureToast } from '@/util/authResolveError';

const ERROR_MESSAGE = `Please try again. If this problem persists, use the forgot password link below or contact support at ${SUPPORT_EMAIL_ADDRESS}`;

export type AuthModalProps = {
  isOpen?: boolean;
};

export function AuthModal({ isOpen }: AuthModalProps) {
  const { navigate } = useRouter();

  const currentPath = useCurrentPagePath();

  const { reset } = useCreateAccount();

  const toast = useToaster();

  const { openEmailLink } = useEmailLink();

  const [showPassword, setShowPassword] = useState(false);

  const [, setShowAuthModal] = useAtom(communityShowAuthModalAtom);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(
      z.object({
        email: z.string().min(1, 'Email is required').email({
          message: 'Invalid email address',
        }),
        password: z.string().min(1, 'Password is required'),
      })
    ),
  });

  const passwordRef = useRef<React.ComponentProps<typeof InputField>>(null);

  const { login } = useAuth();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await login(data);

      if (result.type === 'newPasswordRequired') {
        return navigate(buildAuthPathWithRedirect(ROUTES.NEW_PASSWORD, currentPath) as Href);
      }

      if (result.type === 'unsupportedStep') {
        trackLoginFailed(
          new Error(`Unsupported Cognito signInStep: ${result.step}`),
          data.email,
          'auth_modal'
        );

        return toast.showError(getLoginFailureToast('unsupportedStep'));
      }

      if (result.type === 'transientFailure') {
        // login() already cleared the Cognito session via signOutLocal — show a toast
        // so the modal doesn't silently no-op.
        return toast.showError(getLoginFailureToast('transientFailure'));
      }

      if (result.type === 'persistentFailure') {
        return toast.showError(getLoginFailureToast('persistentFailure'));
      }

      if (result.type === 'success') {
        await trackLoginSuccess(result.user, 'auth_modal');
      }
    } catch (error) {
      console.error('Login ERROR:', error);

      if (error instanceof Error) {
        trackLoginFailed(error, data.email, 'auth_modal');
      }

      let title = 'Sorry, there was a server error';

      if (error instanceof AuthError && error.name === 'NotAuthorizedException') {
        title = 'Incorrect user name or password';
      }

      toast.showError({
        title,
        message: ERROR_MESSAGE,
      });
    }
  });

  const { colors } = useColors();

  const onSignUp = () => {
    reset();

    setShowAuthModal(false);

    const signupPath = buildSignupPathWithRedirect(currentPath);

    navigate(signupPath as Href);
  };

  const modalSize =
    useBreakpointValue({
      default: 'md',
      md: 'sm',
    }) || 'sm';

  return (
    <Modal isOpen={isOpen} size={modalSize} onClose={() => setShowAuthModal(false)}>
      <ModalBackdrop />

      <ModalContent>
        <ModalHeader>
          <Heading size="h2-lg">Sign in to Primum</Heading>

          <ModalCloseButton onPress={() => setShowAuthModal(false)}>
            <Icon as={X} size="md" />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <KeyboardAwareScrollView
            mode="layout"
            bottomOffset={12}
            keyboardShouldPersistTaps="handled"
            disableScrollOnKeyboardHide
            keyboardDismissMode="on-drag"
            showsVerticalScrollIndicator={false}
            contentContainerClassName="flex-col">
            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.email?.message} className="mb-4">
                  <FormControlLabel className="mb-2">
                    <FormControlLabelText>Email</FormControlLabelText>
                  </FormControlLabel>

                  <Input>
                    <InputField
                      autoCorrect={false}
                      style={{ color: colors.typography[900] }}
                      value={value}
                      onChangeText={(text) => onChange(text.toLowerCase())}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      enterKeyHint="next"
                      onSubmitEditing={() => {
                        if (isWeb) {
                          return onSubmit();
                        }

                        // NOTE: gluestack input is not typed correctly
                        // @ts-expect-error
                        passwordRef.current?.focus();
                      }}
                    />
                  </Input>

                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                    <FormControlErrorText size="xs">{errors.email?.message}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl isInvalid={!!errors.password?.message} className="mb-6">
                  <FormControlLabel className="mb-2 justify-between">
                    <FormControlLabelText>Password</FormControlLabelText>

                    <Link
                      onPress={() =>
                        navigate(
                          buildAuthPathWithRedirect(ROUTES.FORGOT_PASSWORD, currentPath) as Href
                        )
                      }>
                      <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
                        Forgot?
                      </LinkText>
                    </Link>
                  </FormControlLabel>

                  <Input>
                    <InputField
                      ref={passwordRef}
                      autoCapitalize="none"
                      style={{ color: colors.typography[900] }}
                      value={value}
                      onChangeText={onChange}
                      type={showPassword ? 'text' : 'password'}
                      enterKeyHint="done"
                      onSubmitEditing={onSubmit}
                    />

                    <InputSlot className="pr-3" onPress={() => setShowPassword((prev) => !prev)}>
                      <InputIcon as={showPassword ? EyeIcon : EyeOffIcon} className="" />
                    </InputSlot>
                  </Input>

                  <FormControlError className="mt-2">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />

                    <FormControlErrorText size="xs">
                      {errors.password?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              )}
            />

            <Button className="mb-6 gap-x-2" onPress={() => onSubmit()} disabled={isSubmitting}>
              {isSubmitting ? <ButtonSpinner color={colors.primary[200]} /> : null}

              <ButtonText>Sign in</ButtonText>
            </Button>

            <HStack className="mb-6 flex-row items-center gap-x-2">
              <Divider className="flex-1" />

              <Text size="xs" className="text-typography-600">
                OR
              </Text>

              <Divider className="flex-1" />
            </HStack>

            <Button onPress={onSignUp} variant="outline">
              <ButtonText>Sign up</ButtonText>
            </Button>
          </KeyboardAwareScrollView>
        </ModalBody>

        <ModalFooter className="mt-4 w-full flex-row items-center justify-center gap-4">
          <Text>Need help?</Text>

          <Link onPress={() => void openEmailLink(SUPPORT_EMAIL_LINK)}>
            <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
              Contact Support
            </LinkText>
          </Link>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
